import { Check, Mail } from 'lucide-react';
import { useEffect } from 'react';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';

/** What the step recorded when the document went out for signature. */
function readEsign(resultJson: string | null): {
  documentId?: string;
  emailedSigners?: string[];
} | null {
  if (!resultJson) return null;
  try {
    const parsed = JSON.parse(resultJson) as {
      esign?: { document_id?: string; emailed_signers?: string[] };
    };
    return parsed.esign
      ? { documentId: parsed.esign.document_id, emailedSigners: parsed.esign.emailed_signers }
      : null;
  } catch {
    return null;
  }
}

/**
 * Read-only eSign step, for the view / review screens.
 *
 * Shows what was SENT, not the signed document: signature status lives in SignCare
 * once the request is raised, and claiming a state here that Studio has not been
 * told about would be a guess.
 */
export function EsignStepDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const esign = readEsign(step.resultJson);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-3">
      {esign?.documentId ? (
        <>
          <p className="flex items-center gap-2 text-sm text-[#166534]">
            <Check size={16} />
            Sent for signature
          </p>
          <dl className="rounded-xl border border-[#E5E7EB] p-3 text-xs">
            <dt className="text-[#666666]">SignCare document</dt>
            <dd className="mt-0.5 break-all font-medium text-[#041A3A]">{esign.documentId}</dd>
          </dl>
          {esign.emailedSigners && esign.emailedSigners.length > 0 ? (
            <p className="flex items-start gap-2 text-xs leading-relaxed text-[#666666]">
              <Mail size={14} className="mt-0.5 shrink-0" />
              <span>Emailed to {esign.emailedSigners.join(', ')}</span>
            </p>
          ) : null}
        </>
      ) : (
        <p className="text-sm text-[#666666]">This document has not been sent for signature yet.</p>
      )}
    </form>
  );
}
